// @flow
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AdvancedSelectField from '@shared/AdvancedSelectField/components/AdvancedSelectField';
import IconDownload from '@icons/components/IconDownload';
import IconFileText from '@icons/components/IconFileText';
import IconCreditCard from '@icons/components/IconCreditCard';
import IconComment from '@icons/components/IconComment';
import IconChartBar from '@icons/components/IconChartBar';
import IconArchiveBox from '@icons/components/IconArchiveBox';

type ExportOption = {
  value: string,
  label: string,
  icon: React$ComponentType<any>,
  onClick: () => void,
  isDanger?: boolean,
};

type Props = {
  onClassExport: () => void,
  onAccountingExport: () => void,
  onArchivePeriods: () => void,
  isArchivingAuthorized: boolean,
  onSmsExport: () => void,
  onKpiExport: () => void,
  isKpiExportLoading: boolean,
};

const ExportActionsMenu = (props: Props): React$Node => {
  const {
    onClassExport,
    onAccountingExport,
    onArchivePeriods,
    isArchivingAuthorized,
    onSmsExport,
    onKpiExport,
    isKpiExportLoading,
  } = props;

  const { t } = useTranslation();

  const options: ExportOption[] = useMemo(() => [
    { value: 'class', label: t('groupClasses.groupClassExport'), icon: IconFileText, onClick: onClassExport },
    { value: 'accounting', label: t('groupClasses.accountingExport'), icon: IconCreditCard, onClick: onAccountingExport },
    { value: 'sms', label: t('groupClasses.smsExport'), icon: IconComment, onClick: onSmsExport },
    { value: 'kpi', label: t('groupClasses.kpiExport'), icon: IconChartBar, onClick: onKpiExport },
    { value: 'archive', label: t('groupClasses.archive'), icon: IconArchiveBox, onClick: onArchivePeriods, isDanger: true },
  ], [
    t,
    onClassExport,
    onAccountingExport,
    onSmsExport,
    onKpiExport,
    onArchivePeriods,
  ]);

  const disabledOptions = useMemo((): string[] => {
    const disabled = [];
    if (!isArchivingAuthorized) disabled.push('archive');
    if (isKpiExportLoading) disabled.push('kpi');
    return disabled;
  }, [isArchivingAuthorized, isKpiExportLoading]);

  const handleChange = useCallback((option: ExportOption | null): void => {
    option?.onClick();
  }, []);

  const formatOptionLabel = useCallback((option: ExportOption): React$Node => {
    const IconComponent = option.icon;
    return (
      <span className={ `export-actions-menu__option${ option.isDanger ? ' export-actions-menu__option--danger' : '' }` }>
        <IconComponent className="export-actions-menu__option-icon" />
        { option.label }
      </span>
    );
  }, []);

  const placeholder = (
    <span className="export-actions-menu__placeholder">
      <IconDownload className="export-actions-menu__placeholder-icon" />
      { t('groupClasses.exports') }
    </span>
  );

  return (
    <div className="export-actions-menu">
      <AdvancedSelectField
        disabledOptions={ disabledOptions }
        formatOptionLabel={ formatOptionLabel }
        isClearable={ false }
        isSearchable={ false }
        name="export-actions"
        onChange={ handleChange }
        options={ options }
        placeholder={ placeholder }
        value={ null }
      />
    </div>
  );
};

export default ExportActionsMenu;
